import { useState, useMemo, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider, CssBaseline } from '@mui/material';
import { useAuthentication } from 'hooks';
import { ThemeContext, ThemeMode } from 'contexts';
import { darkPalette, lightPalette, getComponentOverrides } from 'constants/theme';
import { themeService } from 'services/theme';

function getInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light';
  const stored = localStorage.getItem('themeMode');
  if (stored === 'light' || stored === 'dark') return stored;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface ThemeProviderProps {
  children: React.ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(getInitialMode);
  const { user, userProfile, isAuthenticated } = useAuthentication();

  useEffect(() => {
    try {
      localStorage.setItem('themeMode', themeMode);
    } catch {}
  }, [themeMode]);

  useEffect(() => {
    if (isAuthenticated && userProfile?.settings?.theme) {
      const userTheme = userProfile.settings.theme;
      if (userTheme === 'light' || userTheme === 'dark') {
        setThemeModeState(userTheme);
      }
    }
  }, [isAuthenticated, userProfile?.settings?.theme]);

  const setThemeMode = async (mode: ThemeMode): Promise<void> => {
    setThemeModeState(mode);
    if (isAuthenticated && user?.uid) {
      try {
        await themeService.saveTheme(mode);
      } catch (error) {
        console.error('Failed to save theme preference:', error);
      }
    }
  };

  const toggleTheme = () => {
    const newMode = themeMode === 'light' ? 'dark' : 'light';
    setThemeMode(newMode);
  };

  const isDark = themeMode === 'dark';

  const theme = useMemo(() => {
    const palette = isDark ? darkPalette : lightPalette;
    return createTheme({
      palette,
      components: getComponentOverrides(isDark, palette),
    });
  }, [isDark]);

  return (
    <ThemeContext.Provider value={{ themeMode, setThemeMode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}